import React, { useState, useRef } from 'react';
import './ImageUpload.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

// WordPress-compatible image types
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp';
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const ImageUpload = ({
  value,           // Current image URL
  onChange,        // Callback when image URL changes
  label = 'Image',
  placeholder = 'Click or drag image here',
  className = '',
  compact = false  // Compact mode for smaller displays
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Allowed: JPG, PNG, GIF, WebP';
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `File too large. Maximum size is ${MAX_SIZE_MB}MB`;
    }
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE}/team-stores/admin/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Upload failed');
      }

      const data = await response.json();
      onChange(data.url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`image-upload ${compact ? 'image-upload-compact' : ''} ${className}`}>
      {label && <label className="image-upload-label">{label}</label>}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS}
        onChange={handleFileSelect}
        className="image-upload-input"
      />

      {value ? (
        // Preview mode
        <div className="image-upload-preview">
          <img src={value.startsWith('/') ? `${API_BASE.replace('/api', '')}${value}` : value} alt="Preview" />
          <div className="image-upload-actions">
            <button
              type="button"
              className="image-upload-change"
              onClick={handleClick}
              disabled={uploading}
            >
              {compact ? 'Edit' : 'Change'}
            </button>
            <button
              type="button"
              className="image-upload-remove"
              onClick={handleRemove}
              disabled={uploading}
            >
              {compact ? 'X' : 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        // Drop zone mode
        <div
          className={`image-upload-dropzone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="image-upload-loading">
              <div className="image-upload-spinner"></div>
              {!compact && <span>Uploading...</span>}
            </div>
          ) : (
            <>
              <div className="image-upload-icon">
                <svg width={compact ? "24" : "40"} height={compact ? "24" : "40"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
              </div>
              {!compact && <span className="image-upload-text">{placeholder}</span>}
              {!compact && <span className="image-upload-hint">JPG, PNG, GIF, WebP (max {MAX_SIZE_MB}MB)</span>}
            </>
          )}
        </div>
      )}

      {error && (
        <div className="image-upload-error">{error}</div>
      )}
    </div>
  );
};

export default ImageUpload;
